export const LIGHT_PALETTE_CODES = [
  'default',
  'academic_blue',
  'green_academic',
  'blue_green',
  'purple_academic',
  'sky_academic',
  'teal_academic',
  'violet_academic',
  'pink_rose',
  'pink_fuchsia',
  'orange_academic',
  'orange_amber',
  'gray_slate',
  'gray_graphite',
  'celeste_sky',
  'celeste_aqua',
  'black_onyx',
  'black_charcoal',
  'white_pure',
  'gray_light',
  'white_celeste',
  'white_green',
  'white_red',
  'white_yellow',
  'white_blue',
  'white_purple',
  'kinder_garden',
  'bella_vista'
] as const;

export type LightPaletteCode = (typeof LIGHT_PALETTE_CODES)[number];

export interface LightPaletteTokens {
  '--color-primary': string;
  '--color-secondary': string;
  '--color-accent': string;
  '--color-background': string;
  '--color-surface': string;
  '--color-border': string;
  '--color-border-hover': string;
  '--color-border-focus': string;
  '--color-text-primary': string;
  '--color-text-secondary': string;
  '--color-success': string;
  '--color-warning': string;
  '--color-error': string;
  '--color-white': string;
}

export interface LightPaletteDefinition {
  code: LightPaletteCode;
  nameKey: string;
  descriptionKey: string;
  tokens: LightPaletteTokens;
}

export const DEFAULT_LIGHT_PALETTE_CODE: LightPaletteCode = 'default';

export const LIGHT_PALETTES: ReadonlyArray<LightPaletteDefinition> = [
  {
    code: 'default',
    nameKey: 'profile.sections.colorPalette.options.default.name',
    descriptionKey: 'profile.sections.colorPalette.options.default.description',
    tokens: {
      '--color-primary': '#0d1b3d',
      '--color-secondary': '#1e3a8a',
      '--color-accent': '#2563eb',
      '--color-background': '#f3f4f6',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 12%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 68%, var(--color-primary))',
      '--color-border-focus': '#43657c',
      '--color-text-primary': '#0d1b3d',
      '--color-text-secondary': '#6b7280',
      '--color-success': '#16a34a',
      '--color-warning': '#f59e0b',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'academic_blue',
    nameKey: 'profile.sections.colorPalette.options.academicBlue.name',
    descriptionKey: 'profile.sections.colorPalette.options.academicBlue.description',
    tokens: {
      '--color-primary': '#0b1f4d',
      '--color-secondary': '#1d4ed8',
      '--color-accent': '#2563eb',
      '--color-background': '#eef3fb',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 14%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#355d9a',
      '--color-text-primary': '#0b1f4d',
      '--color-text-secondary': '#5b6f8d',
      '--color-success': '#16a34a',
      '--color-warning': '#f59e0b',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'green_academic',
    nameKey: 'profile.sections.colorPalette.options.greenAcademic.name',
    descriptionKey: 'profile.sections.colorPalette.options.greenAcademic.description',
    tokens: {
      '--color-primary': '#11442f',
      '--color-secondary': '#166534',
      '--color-accent': '#10b981',
      '--color-background': '#edf7f2',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 14%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#2f7a59',
      '--color-text-primary': '#11442f',
      '--color-text-secondary': '#587567',
      '--color-success': '#15803d',
      '--color-warning': '#f59e0b',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'blue_green',
    nameKey: 'profile.sections.colorPalette.options.blueGreen.name',
    descriptionKey: 'profile.sections.colorPalette.options.blueGreen.description',
    tokens: {
      '--color-primary': '#0f2d4f',
      '--color-secondary': '#0f766e',
      '--color-accent': '#0891b2',
      '--color-background': '#eef6f8',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-secondary))',
      '--color-border-focus': '#2d718a',
      '--color-text-primary': '#0f2d4f',
      '--color-text-secondary': '#5e7485',
      '--color-success': '#16a34a',
      '--color-warning': '#f59e0b',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'purple_academic',
    nameKey: 'profile.sections.colorPalette.options.purpleAcademic.name',
    descriptionKey: 'profile.sections.colorPalette.options.purpleAcademic.description',
    tokens: {
      '--color-primary': '#5c2267',
      '--color-secondary': '#8f3ea0',
      '--color-accent': '#aa44ba',
      '--color-background': '#f7f0f8',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#8f4ca0',
      '--color-text-primary': '#4a1f55',
      '--color-text-secondary': '#6f6077',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'sky_academic',
    nameKey: 'profile.sections.colorPalette.options.skyAcademic.name',
    descriptionKey: 'profile.sections.colorPalette.options.skyAcademic.description',
    tokens: {
      '--color-primary': '#1f4f72',
      '--color-secondary': '#2f74a4',
      '--color-accent': '#4488ba',
      '--color-background': '#eef5fb',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#3f7ca7',
      '--color-text-primary': '#1c4664',
      '--color-text-secondary': '#607489',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'teal_academic',
    nameKey: 'profile.sections.colorPalette.options.tealAcademic.name',
    descriptionKey: 'profile.sections.colorPalette.options.tealAcademic.description',
    tokens: {
      '--color-primary': '#1f5a52',
      '--color-secondary': '#2f8f7f',
      '--color-accent': '#44baa2',
      '--color-background': '#eef8f5',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#3f9a88',
      '--color-text-primary': '#1b4f48',
      '--color-text-secondary': '#5c746f',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'violet_academic',
    nameKey: 'profile.sections.colorPalette.options.violetAcademic.name',
    descriptionKey: 'profile.sections.colorPalette.options.violetAcademic.description',
    tokens: {
      '--color-primary': '#35256f',
      '--color-secondary': '#4b3393',
      '--color-accent': '#6044ba',
      '--color-background': '#f2f0fa',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#5d4aaa',
      '--color-text-primary': '#2f2560',
      '--color-text-secondary': '#635f7d',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'pink_rose',
    nameKey: 'profile.sections.colorPalette.options.pinkRose.name',
    descriptionKey: 'profile.sections.colorPalette.options.pinkRose.description',
    tokens: {
      '--color-primary': '#7a1f4d',
      '--color-secondary': '#be185d',
      '--color-accent': '#ec4899',
      '--color-background': '#fdf2f8',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#c14d7f',
      '--color-text-primary': '#6e1d46',
      '--color-text-secondary': '#8a6472',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'pink_fuchsia',
    nameKey: 'profile.sections.colorPalette.options.pinkFuchsia.name',
    descriptionKey: 'profile.sections.colorPalette.options.pinkFuchsia.description',
    tokens: {
      '--color-primary': '#701a58',
      '--color-secondary': '#a21caf',
      '--color-accent': '#d946ef',
      '--color-background': '#fbf1fb',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#b24aa8',
      '--color-text-primary': '#611a4e',
      '--color-text-secondary': '#836079',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'orange_academic',
    nameKey: 'profile.sections.colorPalette.options.orangeAcademic.name',
    descriptionKey: 'profile.sections.colorPalette.options.orangeAcademic.description',
    tokens: {
      '--color-primary': '#7c2d12',
      '--color-secondary': '#c2410c',
      '--color-accent': '#f97316',
      '--color-background': '#fff5ed',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#c06a3f',
      '--color-text-primary': '#6e2a12',
      '--color-text-secondary': '#8a6a5a',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'orange_amber',
    nameKey: 'profile.sections.colorPalette.options.orangeAmber.name',
    descriptionKey: 'profile.sections.colorPalette.options.orangeAmber.description',
    tokens: {
      '--color-primary': '#78350f',
      '--color-secondary': '#b45309',
      '--color-accent': '#f59e0b',
      '--color-background': '#fffaeb',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#c08a3f',
      '--color-text-primary': '#6b3a10',
      '--color-text-secondary': '#877154',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'gray_slate',
    nameKey: 'profile.sections.colorPalette.options.graySlate.name',
    descriptionKey: 'profile.sections.colorPalette.options.graySlate.description',
    tokens: {
      '--color-primary': '#1e293b',
      '--color-secondary': '#475569',
      '--color-accent': '#64748b',
      '--color-background': '#f1f5f9',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#64748b',
      '--color-text-primary': '#1e293b',
      '--color-text-secondary': '#64748b',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'gray_graphite',
    nameKey: 'profile.sections.colorPalette.options.grayGraphite.name',
    descriptionKey: 'profile.sections.colorPalette.options.grayGraphite.description',
    tokens: {
      '--color-primary': '#262626',
      '--color-secondary': '#525252',
      '--color-accent': '#737373',
      '--color-background': '#f5f5f5',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#6b6b6b',
      '--color-text-primary': '#262626',
      '--color-text-secondary': '#6b7280',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'celeste_sky',
    nameKey: 'profile.sections.colorPalette.options.celesteSky.name',
    descriptionKey: 'profile.sections.colorPalette.options.celesteSky.description',
    tokens: {
      '--color-primary': '#075985',
      '--color-secondary': '#0284c7',
      '--color-accent': '#38bdf8',
      '--color-background': '#f0f9ff',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#3f92c0',
      '--color-text-primary': '#0b4a6f',
      '--color-text-secondary': '#5b7488',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'celeste_aqua',
    nameKey: 'profile.sections.colorPalette.options.celesteAqua.name',
    descriptionKey: 'profile.sections.colorPalette.options.celesteAqua.description',
    tokens: {
      '--color-primary': '#0e7490',
      '--color-secondary': '#0891b2',
      '--color-accent': '#22d3ee',
      '--color-background': '#ecfeff',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#3f9cb0',
      '--color-text-primary': '#0b5566',
      '--color-text-secondary': '#5a7d85',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'black_onyx',
    nameKey: 'profile.sections.colorPalette.options.blackOnyx.name',
    descriptionKey: 'profile.sections.colorPalette.options.blackOnyx.description',
    tokens: {
      '--color-primary': '#0a0a0a',
      '--color-secondary': '#262626',
      '--color-accent': '#404040',
      '--color-background': '#f4f4f5',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#52525b',
      '--color-text-primary': '#0a0a0a',
      '--color-text-secondary': '#52525b',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'black_charcoal',
    nameKey: 'profile.sections.colorPalette.options.blackCharcoal.name',
    descriptionKey: 'profile.sections.colorPalette.options.blackCharcoal.description',
    tokens: {
      '--color-primary': '#18181b',
      '--color-secondary': '#3f3f46',
      '--color-accent': '#52525b',
      '--color-background': '#fafafa',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#5b5b63',
      '--color-text-primary': '#18181b',
      '--color-text-secondary': '#71717a',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'white_pure',
    nameKey: 'profile.sections.colorPalette.options.whitePure.name',
    descriptionKey: 'profile.sections.colorPalette.options.whitePure.description',
    tokens: {
      '--color-primary': '#3f3f46',
      '--color-secondary': '#71717a',
      '--color-accent': '#a1a1aa',
      '--color-background': '#ffffff',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#a1a1aa',
      '--color-text-primary': '#27272a',
      '--color-text-secondary': '#71717a',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'gray_light',
    nameKey: 'profile.sections.colorPalette.options.grayLight.name',
    descriptionKey: 'profile.sections.colorPalette.options.grayLight.description',
    tokens: {
      '--color-primary': '#64748b',
      '--color-secondary': '#94a3b8',
      '--color-accent': '#cbd5e1',
      '--color-background': '#f8fafc',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#94a3b8',
      '--color-text-primary': '#334155',
      '--color-text-secondary': '#64748b',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  // White-dominant palettes: a pure white canvas (background + surface) with a
  // single color family driving the secondary/accent tones.
  {
    code: 'white_celeste',
    nameKey: 'profile.sections.colorPalette.options.whiteCeleste.name',
    descriptionKey: 'profile.sections.colorPalette.options.whiteCeleste.description',
    tokens: {
      '--color-primary': '#075985',
      '--color-secondary': '#0ea5e9',
      '--color-accent': '#7dd3fc',
      '--color-background': '#ffffff',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#38a3dc',
      '--color-text-primary': '#0b4a6f',
      '--color-text-secondary': '#5b7488',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'white_green',
    nameKey: 'profile.sections.colorPalette.options.whiteGreen.name',
    descriptionKey: 'profile.sections.colorPalette.options.whiteGreen.description',
    tokens: {
      '--color-primary': '#166534',
      '--color-secondary': '#22c55e',
      '--color-accent': '#86efac',
      '--color-background': '#ffffff',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#3f9a63',
      '--color-text-primary': '#14532d',
      '--color-text-secondary': '#5c7566',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'white_red',
    nameKey: 'profile.sections.colorPalette.options.whiteRed.name',
    descriptionKey: 'profile.sections.colorPalette.options.whiteRed.description',
    tokens: {
      '--color-primary': '#991b1b',
      '--color-secondary': '#ef4444',
      '--color-accent': '#fca5a5',
      '--color-background': '#ffffff',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#c05050',
      '--color-text-primary': '#7f1d1d',
      '--color-text-secondary': '#876262',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#b91c1c',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'white_yellow',
    nameKey: 'profile.sections.colorPalette.options.whiteYellow.name',
    descriptionKey: 'profile.sections.colorPalette.options.whiteYellow.description',
    tokens: {
      '--color-primary': '#854d0e',
      '--color-secondary': '#eab308',
      '--color-accent': '#fde047',
      '--color-background': '#ffffff',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#c09a3f',
      '--color-text-primary': '#713f12',
      '--color-text-secondary': '#847356',
      '--color-success': '#15803d',
      '--color-warning': '#b45309',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'white_blue',
    nameKey: 'profile.sections.colorPalette.options.whiteBlue.name',
    descriptionKey: 'profile.sections.colorPalette.options.whiteBlue.description',
    tokens: {
      '--color-primary': '#1e40af',
      '--color-secondary': '#3b82f6',
      '--color-accent': '#93c5fd',
      '--color-background': '#ffffff',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#4f74d0',
      '--color-text-primary': '#1e3a8a',
      '--color-text-secondary': '#5e6c8a',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  {
    code: 'white_purple',
    nameKey: 'profile.sections.colorPalette.options.whitePurple.name',
    descriptionKey: 'profile.sections.colorPalette.options.whitePurple.description',
    tokens: {
      '--color-primary': '#6b21a8',
      '--color-secondary': '#a855f7',
      '--color-accent': '#d8b4fe',
      '--color-background': '#ffffff',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#9a5fc7',
      '--color-text-primary': '#581c87',
      '--color-text-secondary': '#6f6180',
      '--color-success': '#15803d',
      '--color-warning': '#d97706',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  // Brand palette for the Kinder Garden school: purple + green + sky blue over a
  // soft cream canvas. Its "highlight" yellow (#ffc400) lands on --color-warning,
  // the palette's yellow slot, because the token set is fixed for every palette.
  // --color-success keeps the standard readable green: the brand green (#50c81c)
  // is a background/accent tone, not a legible status color on white.
  {
    code: 'kinder_garden',
    nameKey: 'profile.sections.colorPalette.options.kinderGarden.name',
    descriptionKey: 'profile.sections.colorPalette.options.kinderGarden.description',
    tokens: {
      '--color-primary': '#7426a8',
      '--color-secondary': '#50c81c',
      '--color-accent': '#2ea8f2',
      '--color-background': '#fffdf7',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#9a5fc7',
      '--color-text-primary': '#4a1668',
      '--color-text-secondary': '#6f6178',
      '--color-success': '#15803d',
      '--color-warning': '#ffc400',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  },
  // Brand palette for the Colegio Bella Vista school: the logo is two colors only,
  // the deep navy of the lettering (#033a62) and the gold of the sun and wreath
  // (#fcc603), over a warm ivory canvas that echoes the sunrise in the crest.
  //
  // The gold is split across two token slots because #fcc603 itself is unusable
  // as a foreground: it scores 1.59:1 on white, and --color-secondary always
  // carries white text (--button-secondary-text is var(--color-white)). So the
  // secondary button gets a darkened gold (#9a6c08, 4.64:1 with white — AA), and
  // the literal brand gold lands on --color-warning, the palette's yellow slot.
  // --color-accent is a tint of the brand navy rather than the gold: it doubles
  // as a foreground on white (--button-create-text), where gold cannot go.
  {
    code: 'bella_vista',
    nameKey: 'profile.sections.colorPalette.options.bellaVista.name',
    descriptionKey: 'profile.sections.colorPalette.options.bellaVista.description',
    tokens: {
      '--color-primary': '#033a62',
      '--color-secondary': '#9a6c08',
      '--color-accent': '#0a5c9c',
      '--color-background': '#fcfaf4',
      '--color-surface': '#ffffff',
      '--color-border': 'color-mix(in srgb, var(--color-primary) 13%, var(--color-surface))',
      '--color-border-hover': 'color-mix(in srgb, var(--color-border) 70%, var(--color-primary))',
      '--color-border-focus': '#2f7cb0',
      '--color-text-primary': '#062a46',
      '--color-text-secondary': '#5b6b7c',
      '--color-success': '#15803d',
      '--color-warning': '#fcc603',
      '--color-error': '#dc2626',
      '--color-white': '#ffffff'
    }
  }
];

export const LIGHT_PALETTE_CODE_SET = new Set<LightPaletteCode>(LIGHT_PALETTE_CODES);

export function isLightPaletteCode(value: string | null | undefined): value is LightPaletteCode {
  if (!value) {
    return false;
  }

  return LIGHT_PALETTE_CODE_SET.has(value as LightPaletteCode);
}

export function resolveLightPalette(code: string | null | undefined): LightPaletteDefinition {
  if (!isLightPaletteCode(code)) {
    return LIGHT_PALETTES[0];
  }

  return LIGHT_PALETTES.find((palette) => palette.code === code) ?? LIGHT_PALETTES[0];
}
